"use client";

import { useMemo, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { TextField } from "@/commonTypes/question";
import { validateField } from "@/validation/textFieldSchemas";

export interface FieldError {
  error: boolean;
  errorMessage: string | null;
}

export interface UseFieldValidationReturn {
  /** Validation errors keyed by field ID. Empty fields have no errors until touched. */
  inputErrors: Record<string, FieldError>;
  isAllValid: boolean;
}

/**
 * Derives validation state from field values.
 *
 * Tracks "touched" state: fields that had content and were then cleared
 * will show a required error. Initially empty fields show no error.
 */
export function useFieldValidation(
  inputFields: TextField[],
  values: Record<string, string>,
  questionId: string,
  onValidationChange?: (questionId: string, isValid: boolean) => void,
): UseFieldValidationReturn {
  const t = useTranslations();

  // Track which fields have been "touched" (had content at some point)
  const touchedFieldsRef = useRef<Set<string>>(new Set());

  // Update touched state when values change
  useEffect(() => {
    inputFields.forEach((field) => {
      const value = values[field.id] ?? "";
      if (value.length > 0) {
        touchedFieldsRef.current.add(field.id);
      }
    });
  }, [inputFields, values]);

  // Derive any errors from current values
  const inputErrors = useMemo(() => {
    return inputFields.reduce<Record<string, FieldError>>((acc, inputField) => {
      const value = values[inputField.id] ?? "";
      const isTouched = touchedFieldsRef.current.has(inputField.id);

      // Empty field: optional fields are always valid when empty,
      // required fields show error only if previously touched
      if (value.length === 0) {
        if (inputField.optional) {
          acc[inputField.id] = { error: false, errorMessage: null };
        } else if (isTouched) {
          acc[inputField.id] = {
            error: true,
            errorMessage: t("validation.required"),
          };
        } else {
          acc[inputField.id] = { error: false, errorMessage: null };
        }
        return acc;
      }

      const errorMessage = validateField(value, inputField.validationType, t);

      acc[inputField.id] = {
        error: errorMessage !== null,
        errorMessage,
      };
      return acc;
    }, {});
  }, [inputFields, values, t]);

  // Derive overall validity: optional empty fields are always valid,
  // all other fields must have no errors
  const isAllValid = useMemo(() => {
    return inputFields.every((inputField) => {
      const value = values[inputField.id] ?? "";
      if (inputField.optional && value.length === 0) return true;
      return !inputErrors[inputField.id]?.error;
    });
  }, [inputFields, inputErrors, values]);

  // Notify parent when validity changes
  const prevIsValidRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevIsValidRef.current !== isAllValid) {
      prevIsValidRef.current = isAllValid;
      onValidationChange?.(questionId, isAllValid);
    }
  }, [isAllValid, questionId, onValidationChange]);

  return { inputErrors, isAllValid };
}

